import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminApi from "Service/Admin.api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import { StyledTableCell } from "./ProfessionalUserList";
import { IndianStates } from "Constants/Arrays";
import styled from "@emotion/styled";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";

/**
 * Interface for the address data received from the user object.
 */
interface UserAddress {
  address: string;
  city: string;
  state: string; // This is the state code (e.g., 'AN')
  postalCode: string;
}

/**
 * Interface for the state objects in the IndianStates array.
 */
interface StateOption {
  id: string;
  label: string; // This is the full state name (e.g., 'Andaman and Nicobar Islands')
  value: string;
}

/**
 * Helper function to format a user's address into a readable string.
 * It looks up the full state name from the state code.
 *
 * @param addressData - The address object containing address, city, state code, and postalCode.
 * @param stateOptions - The array of state lookup objects (IndianStates).
 * @returns A formatted address string, or 'Address Not Available' if critical data is missing.
 */
export const formatUserAddress = (
  addressData: UserAddress,
  stateOptions: StateOption[] // Pass the IndianStates array here
): string => {
  // Destructure for easier access
  const { address, city, state, postalCode } = addressData;

  // Basic check for required fields
  if (!address || !city || !state || !postalCode) {
    return 'Address Not Available';
  }

  // Find the full state name using the state code
  const stateEntry = stateOptions.find(opt => opt.value === state);
  const fullStateName = stateEntry ? stateEntry.label : state; // Use code as fallback

  // Construct the formatted string, using line breaks (<br />) for display in a TableCell
  const formattedAddress =
    `${address}, ${city} ` +
    `${fullStateName} - ${postalCode}`;

  return formattedAddress;
};

interface CaseDetailsType {
  pkCase: number;
  caseNumber: string;
  clientId: number;
  clientFullName: string;
  caseTypeId: number;
  caseTypeName: string;
  orderTypeId: number;
  orderTypeName: string;
  casePrice: number;
  paymentId: number | null;
  currentProcessingStatusId: number;
  currentProcessingStatusName: string;
  description: string | null;
  dateCreated: string;
  dateUpdated: string | null;
  statusLastUpdated: string;
  createdByUserId: number;
  createdByUserName: string;
  updatedByUserId: number | null;
  updatedByUserName: string | null;
  assignedProfessionalId: number | null;
  assignedProfessionalName: string | null;
  claimAmount: number | null;
  agreementDate: string | null;
  dueDateOfPayment: string | null;
  courtClauseReference: string | null;
  customerRoleInCase: string | null;
  isBulkUploadCase: boolean;
  uploadBatchNumber: number | null;
  isConsentGivenToUs: boolean;
  bulkCaseReferenceNumber: string | null;
}

interface DetailItemProps {
  label: string;
  value: any;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: "18px", color: "#777" }}>{label}</div>
    <div
      style={{
        fontSize: "16px",
        fontWeight: "500",
        marginTop: "3px",
      }}
    >
      {value}
    </div>
  </div>
);

const AssignButton = styled(Button)(({ theme }) => ({
  // borderRadius: theme.spacing(3),
  textTransform: "none",
  fontWeight: "bold",
  // padding: theme.spacing(1, 3),
  background: ColorData.primary.main, // <-- Use secondary color for background
  color: ColorData.text.secondary, // <-- Set contrasting text color (black)
  "&:hover": {
    background: ColorData.hover.main, // <-- Use hover color for background on hover
    transform: "translateY(-1px)",
    boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
    color: ColorData.text.primary, // <-- Maintain contrast on hover too
  },
}));

const CaseDetails = () => {
  const { caseId } = useParams();
  const numericCaseId: string | number = Number(caseId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  // 1. STATE MANAGEMENT
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [caseData, setCaseData] = useState<CaseDetailsType | null>(null);
  const [error, setError] = useState(null);
  const [assignLoading, setAssignLoading] = useState<Record<string, boolean>>({});

  // Fetch case details using caseId
  const fetchCaseDetails = async () => {
    try {
      dispatch(startLoading({ loading: true, loadtext: "Loading case details..." }));

      const res = await AdminApi.fetchCaseDetails(numericCaseId);

      if (res?.success) {
        dispatch(stopLoading());
        setCaseData(res.data);
      } else {
        console.error("Error fetching data:", error);
        dispatch(stopLoading());
        dispatch(
          setAlertData({
            alertMessage: "Error fetching case details",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (err) {
      console.error("Error fetching case details:", err);
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Error fetching case details",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  // Fetch professional users
  const fetchProfessionals = async () => {
    try {
      dispatch(startLoading({ loading: true, loadtext: "Loading professionals..." }));

      const response = await AdminApi.GetAllActiveProfessionalUser();

      if (response?.success && Array.isArray(response.data)) {
        dispatch(stopLoading());
        setUsers(response.data);
      }
    } catch (error) {
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Error fetching professionals",
          alertVariant: "error",
          openAlert: true,
        })
      );

      console.error("Error fetching professionals:", error);
    }
  };

  // Load case details when caseId changes
  useEffect(() => {
    if (!isNaN(numericCaseId)) {
      fetchCaseDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericCaseId]);

  // Load professionals list once
  useEffect(() => {
    fetchProfessionals();
  }, []);

  const handleAssignProfessional = async (caseId: string, professionalId: string) => {
    try {
      dispatch(startLoading(
        {
          loading: true,
          loadtext: "Assigning Professional...",
        }
      ))
      const AssignCaseToProfessional = await AdminApi.AssignCaseToProfessional({
        caseId: caseId,
        assignedProfessionalId: professionalId,
      })

      if (AssignCaseToProfessional?.success) {
        dispatch(stopLoading());
        dispatch(
          setAlertData({
            alertMessage: AssignCaseToProfessional?.alert.alertMessage,
            alertVariant: AssignCaseToProfessional?.alert.alertVariant,
            openAlert: AssignCaseToProfessional?.alert.openAlert,
          })
        );
      }
      fetchCaseDetails();
    } catch (error) {
      console.error("Error assigning professional:", error);
      return;
    }
  };

  // 3. OPTIMIZED SEARCH (The useMemo part)
  const displayedUsers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return users;

    return users.filter((user: any) => {
      const firstName = (user?.firstName ?? "").toLowerCase();
      const caseNumber = (user?.caseNumber ?? "").toLowerCase();
      const lastName = (user?.lastName ?? "").toLowerCase();
      const fullName = `${firstName} ${lastName}`;
      const phone = (user?.contactNumber ?? "").toLowerCase();
      const qualification = (user?.highestQualification ?? "").toLowerCase();
      // Convert ID to string safely so .includes() works
      const city = (user?.city ?? "").toLowerCase();
      const state = (user?.state ?? "").toLowerCase();
      const address = (user?.address ?? "").toLowerCase();
      const userId = String(user?.id ?? "");

      // Check if the query matches ANY skill name in the skills array
      const hasMatchingSkill = user?.skills?.some((skill: any) =>
        (skill?.skillName ?? "").toLowerCase().includes(q)
      );

      // Check if the query matches ANY role in the roles array
      const hasMatchingRole = user?.roles?.some((role: string) =>
        role.toLowerCase().includes(q)
      );

      // --- THE FILTER TEST ---
      return (
        fullName.includes(q) ||
        phone.includes(q) ||
        qualification.includes(q) ||
        userId.includes(q) ||
        hasMatchingSkill ||
        hasMatchingRole ||
        caseNumber.includes(q) ||
        city.includes(q) ||
        state.includes(q) ||
        address.includes(q)
      );
    });
  }, [searchQuery, users]);

  const formatSkillServiceArea = (
    skill: any,
    stateOptions: { value: string; label: string }[]
  ): string => {
    if (skill.servesPanIndia) return "Yes (Pan India)";

    if (!skill.servesStates) return "No States Listed";

    // Explicit typing added here ↓↓↓
    const stateCodes: string[] = skill.servesStates
      .split(",")
      .map((code: string) => code.trim());

    const stateNames = stateCodes
      .map((code: string) => {
        const match = stateOptions.find(
          (s) => s.value === code
        );
        return match ? match.label : code;
      })
      .join(", ");

    return stateNames;
  };



  return (
    <Box
      sx={{
        py: 4,
        pb: 12,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflowY: "auto",
        padding: 1,
      }}
    >

      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "transparent",
          border: "none",
          color: "#000000ff",
          fontSize: "16px",
          fontWeight: "500",
          cursor: "pointer",
          marginBottom: "15px",
        }}
      >
        <ArrowBackIcon />
        Back
      </button>

      <Container
        sx={{
          py: 4,
          pb: 12,
        }}
      >
        {/* CASE DETAILS CARD */}
        <Box
          sx={{
            background: "#fff",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            mb: 4,
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Case Details</h2>
          {/* <p style={{ fontSize: "16px", color: "#000", fontWeight: "700" }}>Case ID: {caseId}</p> */}
          <p style={{ fontSize: "16px", color: "#000", fontWeight: "700" }}>Case Number: {caseData?.caseNumber || "Not Available"}</p>

          {error && <p style={{ color: "red" }}>{error}</p>}

          {caseData && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginTop: "20px",
                }}
              >
                <DetailItem label="Client Name" value={caseData.clientFullName} />
                <DetailItem label="Case Type" value={caseData.caseTypeName} />
                <DetailItem label="Order Type" value={caseData.orderTypeName} />
                <DetailItem label="Case Price" value={`₹${caseData.casePrice}`} />
                <DetailItem
                  label="Current Status"
                  value={caseData.currentProcessingStatusName}
                />
                <DetailItem
                  label="Claim Amount"
                  value={caseData.claimAmount || "—"}
                />
                <DetailItem
                  label="Agreement Date"
                  value={caseData.agreementDate || "—"}
                />
                <DetailItem
                  label="Due Date"
                  value={caseData.dueDateOfPayment || "—"}
                />
                <DetailItem
                  label="Assigned Professional"
                  value={caseData.assignedProfessionalName || "Not Assigned"}
                />
                <DetailItem
                  label="Created By"
                  value={caseData.createdByUserName}
                />
                <DetailItem
                  label="Created On"
                  value={new Date(caseData.dateCreated).toLocaleString()}
                />
              </div>

              {/* DESCRIPTION */}
              <div style={{ marginTop: "25px" }}>
                <h4 style={{ marginBottom: "8px" }}>Description</h4>
                <p
                  style={{
                    background: "#f9f9f9",
                    padding: "15px",
                    borderRadius: "8px",
                  }}
                >
                  {caseData.description || "No description available"}
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>


      {/* ASSIGN PROFESSIONAL TITLE */}
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{
          padding: 2,
          borderRadius: 2,
          backgroundColor: (theme) => ColorData.primary.main,
          color: (theme) => ColorData.text.secondary,
        }}
      >
        Assign Professional to Case
      </Typography>

      {/* Search and Actions */}
      <Grid container spacing={3} sx={{ mb: 12, }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search professional User..."
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box display="flex" gap={1} height="100%" alignItems="center">
            <Chip
              icon={<PersonIcon />}
              label={`Total Users: ${users.length}`}
              color="info"
              variant="outlined"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                height: { xs: 28, sm: 32 }
              }}
            />
          </Box>
        </Grid>


        {/* PROFESSIONAL TABLE */}
        <Paper sx={{
          mt: 2,
          mb: 3,
          boxSizing: "border-box",
          width: "100%",

        }} >
          <TableContainer
            sx={{
              maxHeight: 450,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>User ID</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Mobile</StyledTableCell>
                  <StyledTableCell>Address</StyledTableCell>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell>Highest Qualification</StyledTableCell>
                  <StyledTableCell>Skill</StyledTableCell>
                  <StyledTableCell align="center">Action</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedUsers.length === 0 ? (

                  <TableRow>
                    <TableCell colSpan={8} align="center"  >
                      No Professional Users Found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedUsers.map((user: any, index: number) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{user.contactNumber}</TableCell>

                      <TableCell>{formatUserAddress(user, IndianStates)}</TableCell>
                      <TableCell>{user.description}</TableCell>
                      <TableCell>{user.highestQualification}</TableCell>
                      <TableCell>
                        {user.skills && user.skills.length > 0 ? (
                          user.skills.map((skill: any) => (
                            <div
                              key={skill.pkSkillProfessionalMapping}
                              style={{
                                marginBottom: "8px",
                                padding: "10px",
                                background: "#f7f7f7",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              {/* Skill Name + Experience */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div style={{ fontWeight: 600, fontSize: "14px" }}>
                                  {skill.skillName}
                                </div>

                                <div
                                  style={{
                                    fontSize: "12px",
                                    background: "#e3f2fd",
                                    color: "#1565c0",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {skill.numberOfExperienceYears} yrs
                                </div>
                              </div>

                              {/* Service Area */}
                              <div
                                style={{
                                  marginTop: "6px",
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  borderRadius: "20px",
                                  background: "#e8f5e9",
                                  color: "#2e7d32",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {formatSkillServiceArea(skill, IndianStates)}
                              </div>
                            </div>
                          ))
                        ) : (
                          "No Skills Listed"
                        )}
                      </TableCell>

                      {/* Assign button */}
                      <AssignButton
                        variant="contained"
                        fullWidth
                        onClick={() => handleAssignProfessional(String(numericCaseId), user.id)}
                        disabled={
                          !!caseData?.assignedProfessionalId || assignLoading[String(caseId)]
                        }
                        startIcon={
                          assignLoading[numericCaseId] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <AssignmentIcon />
                          )
                        }
                        sx={{ mt: 2 }}
                      >
                        {assignLoading[numericCaseId] ? "Assigning..." : "Assign Professional"}
                      </AssignButton>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Box>
  );
};

export default CaseDetails;